import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TuiAlertService, TuiNotification } from '@taiga-ui/core';
import { ServicesService } from 'src/app/services/service/services.service';
import { Subscription } from 'rxjs';
import { PendingServiceRow } from 'src/app/models/pending-services/pending-services.model';
import { Zone } from 'src/app/models/zone/zone.model';
import { BaseInfo } from './interfaces/send-message.model';
import { TranslateService } from '@ngx-translate/core';

export type SendMessageMethod = 'SMS' | 'WhatsApp';

@Component({
  selector: 'app-modal-send-message',
  templateUrl: './modal-send-message.component.html',
  styleUrls: ['./modal-send-message.component.css'],
})
export class ModalSendMessageComponent implements OnInit, OnDestroy {

  @Input() sendType: SendMessageMethod = 'SMS';
  @Input() serviciosSeleccionados: PendingServiceRow[] = [];
  @Input() zones: Zone[] = [];
  @Input() baseInfo: BaseInfo = { count: 0 };
  @Output() finished = new EventEmitter<boolean>();

  isSending = false;
  private subscription = new Subscription();

  constructor(
    private readonly offcanvas: NgbOffcanvas,
    private readonly alert: TuiAlertService,
    private readonly servicesSvc: ServicesService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.serviciosSeleccionados.forEach((s) => {
      if (s.deleted === undefined) {
        s.deleted = false;
      }
    });
  }

  get remaining(): PendingServiceRow[] {
    return this.serviciosSeleccionados.filter((s) => !s.deleted);
  }

  get numSelectedServices(): number {
    return this.remaining.length;
  }

  translateProduct(name: string): string {
    const key = 'common.services.' + name;
    const translated = this.translate.instant(key);
    return translated === key ? name : translated;
  }

  deleteService(svc: PendingServiceRow): void {
    svc.deleted = true;
  }

  undoDeleteService(svc: PendingServiceRow): void {
    svc.deleted = false;
  }

  trackByOrderHash(_: number, service: PendingServiceRow): string {
    return service.orderHash;
  }

  closeCanvas(): void {
    this.offcanvas.dismiss();
  }

  sendMessage(): void {
    if (this.remaining.length === 0) {
      this.alert
        .open(
          this.translate.instant(
            'modals.modal-send-message.no_services_selected'
          ),
          { status: 'warning' }
        )
        .subscribe();
      return;
    }

    const payload = {
      orderHashes: this.remaining.map((s) => s.orderHash),
      sendMessageService: this.sendType,
    };

    this.isSending = true;

    this.subscription.add(
      this.servicesSvc.sendMessage(payload).subscribe({
        next: (res) => {
          this.isSending = false;
          if (res.wasSuccessful) {
            this.offcanvas.dismiss();
            const count = this.remaining.length;

            const labelKey =
              this.sendType === 'SMS'
                ? count === 1
                  ? 'modals.modal-send-message.sms_label_single'
                  : 'modals.modal-send-message.sms_label_plural'
                : count === 1
                ? 'modals.modal-send-message.whatsapp_label_single'
                : 'modals.modal-send-message.whatsapp_label_plural';
            const label = this.translate.instant(labelKey, { count });

            const textKey =
              this.sendType === 'SMS'
                ? count === 1
                  ? 'modals.modal-send-message.sms_text_single'
                  : 'modals.modal-send-message.sms_text_plural'
                : count === 1
                ? 'modals.modal-send-message.whatsapp_text_single'
                : 'modals.modal-send-message.whatsapp_text_plural';
            const text = this.translate.instant(textKey, { count });

            this.alert
              .open(text, {
                label,
                status: 'success',
              })
              .subscribe();

            this.finished.emit(true);
          } else {
            this.alert
              .open(
                this.translate.instant(
                  'modals.modal-send-message.error_sending'
                ),
                { status: 'error' }
              )
              .subscribe();
            this.finished.emit(false);
          }
        },
        error: () => {
          this.isSending = false;
          this.alert
            .open(
              this.translate.instant('modals.modal-send-message.error_sending'),
              { status: 'error' }
            )
            .subscribe();
          this.finished.emit(false);
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
